import authService from './authService';
import { SalesChannel, SalesChannelFormData, SalesChannelSearchModel } from '../types/salesChannel';
import { PaginatedResponse, ApiResponse } from '../types/common';

class SalesChannelService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  async getAll(searchModel: SalesChannelSearchModel): Promise<PaginatedResponse<SalesChannel>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/sales-channels?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar canais de venda');
    }

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getById(id: number): Promise<SalesChannel> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/sales-channels/${id}`);

    if (!response.ok) {
      if (response.status === 404) throw new Error('Canal de venda não encontrado');
      throw new Error('Falha ao buscar canal de venda');
    }

    const data: ApiResponse<SalesChannel> = await response.json();
    return data.data!;
  }

  async create(formData: SalesChannelFormData): Promise<SalesChannel> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/sales-channels', formData);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar canal de venda');
    }

    const data: ApiResponse<SalesChannel> = await response.json();
    return data.data!;
  }

  async update(id: number, formData: SalesChannelFormData): Promise<SalesChannel> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/sales-channels/${id}`, formData);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar canal de venda');
    }

    const data: ApiResponse<SalesChannel> = await response.json();
    return data.data!;
  }

  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/sales-channels/${id}`);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir canal de venda');
    }
  }

  /** Returns all sales channels (unpaginated) for use in selects/dropdowns */
  async listAll(): Promise<SalesChannel[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/sales-channels?page=1&page_size=100');

    if (!response.ok) {
      throw new Error('Falha ao buscar canais de venda');
    }

    const json = await response.json();
    return Array.isArray(json.data) ? json.data : [];
  }
}

export const salesChannelService = new SalesChannelService();
export default salesChannelService;
